import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, getErrorMessage } from '../types';
import { useTranslation } from '../i18n';

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
}

interface ExternalLoginProps {
  onLogin: (accessToken: string, refreshToken: string) => void;
}

const inFlightExternalLogins = new Map<string, Promise<AuthResponse>>();

function completeExternalLogin(provider: string, code: string): Promise<AuthResponse> {
  const key = `${provider}:${code}`;
  const existing = inFlightExternalLogins.get(key);
  if (existing) return existing;

  const promise = axios
    .post<AuthResponse>(`${API_URL}/auth/external/${provider}/login`, { code })
    .then((res) => res.data)
    .catch((err) => {
      inFlightExternalLogins.delete(key);
      throw err;
    });

  inFlightExternalLogins.set(key, promise);
  return promise;
}

export const ExternalLogin: React.FC<ExternalLoginProps> = ({ onLogin }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const provider = searchParams.get('provider');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(Boolean(code && provider));

  useEffect(() => {
    if (!code || !provider) return;

    let cancelled = false;
    (async () => {
      try {
        const data = await completeExternalLogin(provider, code);
        if (cancelled) return;
        onLogin(data.accessToken, data.refreshToken);
        navigate('/', { replace: true });
      } catch (err) {
        if (cancelled) return;
        setMessage(getErrorMessage(err, t('social.login_error_generic')));
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, provider, navigate, onLogin, t]);

  if (!code || !provider) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h2>{t('social.login_title')}</h2>
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {t('social.error_missing_code')}
          </div>
          <Link to="/login" className="btn btn-primary" style={{ width: '100%', textAlign: 'center' }}>
            {t('social.back_to_login')}
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h2>{t('social.login_title')}</h2>
          <p className="login-subtitle">{t('social.login_completing')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <h2>{t('social.login_title')}</h2>
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {message}
        </div>
        <Link to="/login" className="btn btn-primary" style={{ width: '100%', textAlign: 'center' }}>
          {t('social.back_to_login')}
        </Link>
      </div>
    </div>
  );
};
